import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../api-config';
import {
  ApiResponse,
  Country,
  HistoryAction,
  PageableDto,
  PageResponse,
} from '@shared-models';
import { mapPageableResponse } from '../../utils/map-pageable';

export type { Country };

/** @deprecated Usar `PageResponse<Country>` de `@shared-models`. */
export type CountryPageResponse = PageResponse<Country>;

/** @deprecated Preferir `HistoryAction` de `@shared-models`. */
export type CountryHistoryAction = HistoryAction;

/** Página de historial de país (mantiene totalElements para el paginador). */
export type CountryHistoryPageResponse = PageResponse<HistoryAction>;

@Injectable({ providedIn: 'root' })
export class CountryService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/countries`;
  }

  /**
   * Normaliza el status - mantiene el valor del API tal como viene
   */
  private normalizeStatus(status: string | undefined): string {
    if (!status) return 'ACTIVE';
    return status.toUpperCase() as string;
  }

  /**
   * Transforma la respuesta del backend al modelo del frontend
   */
  private mapToCountry(item: any): Country {
    return {
      id: item.id,
      iso: item.iso || '',
      name: item.name || '',
      largeName: item.largeName || '',
      numberIso: item.numberIso || '',
      largeIso: item.largeIso || '',
      timezone: item.timezone || '',
      lada: item.lada || '',
      status: this.normalizeStatus(item.status),
      continentId: item.continentId || '',
      sbs: item.sbs || '',
      flagUrl: item.flagUrl || '',
    };
  }

  /**
   * Obtiene todos los países (con paginación del backend)
   */
  getAll(
    page = 0,
    size = 20,
    sort = 'name',
    direction: 'asc' | 'desc' = 'asc',
    search?: string,
    status?: string,
  ): Observable<CountryPageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status && status.trim()) {
      params = params.set('status', status.trim());
    }

    return this.httpClient
      .get<ApiResponse<PageableDto>>(this.apiUrl, { params })
      .pipe(
        map((response) =>
          mapPageableResponse(
            response,
            (item) => this.mapToCountry(item),
            size,
          ),
        ),
      );
  }

  /**
   * Obtiene un país por ID
   */
  getById(id: string | number): Observable<Country> {
    return this.httpClient
      .get<ApiResponse<any>>(`${this.apiUrl}/${id}`)
      .pipe(map((response) => this.mapToCountry(response.data)));
  }

  /**
   * Crea un nuevo país
   * La API devuelve solo { message: "..." } sin data
   */
  create(data: Omit<Country, 'id'>): Observable<{ message: string }> {
    return this.httpClient
      .post<{ success: boolean; message: string }>(this.apiUrl, data)
      .pipe(
        map((response) => ({
          message: response.message || 'País creado exitosamente',
        })),
      );
  }

  /**
   * Actualiza un país existente
   * La API devuelve solo { message: "..." } sin data
   */
  update(
    id: string | number,
    data: Partial<Country>,
  ): Observable<{ message: string }> {
    return this.httpClient
      .put<{ success: boolean; message: string }>(`${this.apiUrl}/${id}`, data)
      .pipe(
        map((response) => ({
          message: response.message || 'País actualizado exitosamente',
        })),
      );
  }

  /**
   * Actualiza un país con imagen de bandera
   * La API devuelve solo { message: "..." } sin data
   */
  updateWithFile(
    id: string | number,
    formData: FormData,
  ): Observable<{ message: string }> {
    return this.httpClient
      .put<{
        success: boolean;
        message: string;
      }>(`${this.apiUrl}/${id}`, formData)
      .pipe(
        map((response) => ({
          message: response.message || 'País actualizado exitosamente',
        })),
      );
  }

  /**
   * Elimina un país
   * La API devuelve solo { message: "..." } sin data
   */
  delete(id: string | number): Observable<{ message: string }> {
    return this.httpClient
      .delete<{ success: boolean; message: string }>(`${this.apiUrl}/${id}`)
      .pipe(
        map((response) => ({
          message: response.message || 'País eliminado exitosamente',
        })),
      );
  }

  /**
   * Obtiene el historial de acciones de un país (paginación server-side).
   *
   * Endpoint:
   *   GET /api/settings/countries/{id}/history?page=&size=&sort=&direction=&search=
   *
   * Devuelve una `CountryHistoryPageResponse` con los items de la página
   * solicitada + metadata de paginación (`totalElements`, `totalPages`,
   * `page`, `size`) para que el componente pueda renderizar el paginador
   * sin tener que adivinar el total. Mismo shape que el resto de history
   * endpoints del proyecto (CompanyHistoryService, GroupService, etc.).
   *
   * Defaults:
   *   - page: 0 (zero-based, como el resto de services)
   *   - size: 20
   *   - sort: 'date' (default backend)
   *   - direction: 'desc' (más reciente primero, que es lo que tiene
   *     sentido en un historial)
   *   - search: undefined (no se envía query param si está vacío, para
   *     no forzar al backend a interpretar `search=` como filtro
   *     "todos los que matcheen string vacío")
   */
  getHistory(
    id: string | number,
    page = 0,
    size = 20,
    sort = 'date',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
  ): Observable<CountryHistoryPageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }

    return this.httpClient
      .get<ApiResponse<PageableDto>>(this.apiUrl + `/${id}/history`, {
        params,
      })
      .pipe(
        map((response) =>
          mapPageableResponse(
            response,
            (action: any): HistoryAction => ({
              id: action.id,
              date: action.date,
              action: action.action || 'Acción',
              user: action.user || 'Usuario',
              ip: action.ip || '',
              device: action.device || '',
              browser: action.browser || '',
              location: action.location || '',
            }),
            size,
          ),
        ),
      );
  }

  /**
   * Actualiza el status de un país
   * La API devuelve solo { message: "..." } sin data
   */
  updateStatus(
    id: string | number,
    status: 'active' | 'inactive',
  ): Observable<{ message: string }> {
    return this.httpClient
      .patch<{
        success: boolean;
        message: string;
      }>(`${this.apiUrl}/${id}/status`, { status })
      .pipe(
        map((response) => ({
          message: response.message || 'Status actualizado exitosamente',
        })),
      );
  }

  /**
   * Exporta la lista de países a PDF
   * La API genera y descarga el PDF directamente
   */
  exportPdf(options?: {
    title?: string;
    status?: string;
    search?: string;
    page?: number;
    size?: number;
  }): Observable<Blob> {
    const params = new HttpParams()
      .set('title', options?.title || 'Reporte de países')
      .set('status', options?.status || '')
      .set('search', options?.search || '')
      .set('page', (options?.page ?? 0).toString())
      .set('size', (options?.size ?? 1000).toString());

    return this.httpClient.get(`${this.apiConfig.apiUrl}/export/pdf/country`, {
      params,
      responseType: 'blob',
    });
  }

  /**
   * Exporta la lista de países a Excel
   * La API genera y descarga el archivo Excel directamente
   */
  exportExcel(options?: {
    title?: string;
    status?: string;
    search?: string;
    page?: number;
    size?: number;
  }): Observable<Blob> {
    const params = new HttpParams()
      .set('title', options?.title || 'Reporte de países')
      .set('status', options?.status || '')
      .set('search', options?.search || '')
      .set('page', (options?.page ?? 0).toString())
      .set('size', (options?.size ?? 1000).toString());

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/excel/country`,
      {
        params,
        responseType: 'blob',
      },
    );
  }

  /**
   * Exporta el historial de un país a PDF
   */
  exportHistoryPdf(
    id: string | number,
    options?: {
      title?: string;
    },
  ): Observable<Blob> {
    const params = new HttpParams().set(
      'title',
      options?.title || 'Historial de país',
    );

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/pdf/history/country/${id}`,
      {
        params,
        responseType: 'blob',
      },
    );
  }

  /**
   * Exporta el historial de un país a Excel
   */
  exportHistoryExcel(
    id: string | number,
    options?: {
      title?: string;
    },
  ): Observable<Blob> {
    const params = new HttpParams().set(
      'title',
      options?.title || 'Historial de país',
    );

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/excel/history/country/${id}`,
      {
        params,
        responseType: 'blob',
      },
    );
  }
}
